/**
 * Suns — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_motifs_ii`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const sunsPack: ToilePack = {
    slug: 'suns',
    category: 'motif',
    name: 'Suns',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 832},
    readings: [
        {key: 'face', label: 'Sun with a face', subject: 'A sun with a calm face — closed eyes, a slight smile — and alternating straight and wavy rays, centred on a plain ground.'},
        {key: 'boho', label: 'Boho sun', subject: 'A boho sun with thick triangular rays in terracotta and a plain disc, a few small dots between the rays, on a cream ground with a small arch beneath.'},
        {key: 'rising', label: 'Sunrise', subject: 'A half sun rising over a flat horizon line with rays fanning up, a few birds, a minimal composition on a plain ground.'},
    ],
    styles: [
        {key: 'flat', label: 'Flat', style: 'flat vector illustration, bold simplified shapes, limited palette, plain ground', palette: 'a single bold yellow on white, no gradients', framing: 'centred on a plain white background with generous padding around the shape'},
        {key: 'tattoo', label: 'Tattoo linework', style: 'fine-line tattoo design, black ink only, clean weighted outlines, dotwork shading, white ground', palette: 'black ink only, no other colour', framing: 'centred on a plain white background with a clear margin around the design'},
        {key: 'boho', label: 'Boho', style: 'boho print, terracotta and cream, textured paper, hand-drawn', palette: 'warm terracotta and cream, no other colour', framing: 'set on a cream textured background with a hand-drawn border'},
    ],
    directions: ['a moon to match', 'with rainbow rays', 'for a nursery wall'],
    directionHint: 'try: a moon to match',
    seo: {
        title: 'Sun art: with a face, boho, sunrise in three styles',
        metaDescription: 'Nine sun illustrations — a sun with a face, a boho terracotta sun and a sunrise — in flat, tattoo linework and boho print styles.',
        h1: 'Sun drawings: face, boho, sunrise in three styles',
        tagline: 'Nine renders, three sun readings, three looks each — flat icon to boho print.',
        intro: 'The disc itself, where sunsets already own the scene: a sun with a calm smiling face, a boho sun in thick terracotta rays, and a half sun rising over a horizon. Each renders flat and bold, as fine tattoo linework, or in a hand-drawn terracotta boho print. Ask for a matching moon or rainbow rays and the same three looks carry it.',
        useCases: [
            'a boho nursery or wall print',
            'a tattoo flash reference sheet',
            'a gift card or sticker design',
            'a social media graphic',
        ],
        faq: [
            {q: 'What size do these print at?', a: 'Each is composed as a clean square crop, suited to a print, a card or a tattoo reference.'},
            {q: 'Can I get a matching moon or rainbow rays?', a: 'Yes — the direction field takes any variation, and the same three styles render it.'},
            {q: "What's the difference between the three styles?", a: 'Flat is a single bold yellow shape on white, tattoo linework is black-ink dotwork shading, and boho is terracotta and cream on textured paper.'},
            {q: 'Is this a real photograph of the sun or a sunset?', a: 'No — every image is an illustration of the sun as a motif, not a photo.'},
        ],
    },
}
